/*!
 * Created by netlogix GmbH & Co. KG
 *
 * @copyright netlogix GmbH & Co. KG
 */

import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DriverDetailComponent } from '@drivers/driver-detail/driver-detail.component';
import { DriversService } from '@drivers/services/drivers.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import { Driver, User } from '@shared/models';
import { merge, of } from 'rxjs';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';
import * as DriversActions from './drivers.actions';
import { Store } from '@ngrx/store';
import { State } from '@core/store';
import {
  DriverDeactivationConfirmationComponent
} from '@drivers/driver-deactivation-confirmation/driver-deactivation-confirmation.component';

@Injectable()
export class DriversEffects {
  constructor(
    private actions$: Actions<DriversActions.DriversActions>,
    private driversService: DriversService,
    private translateService: TranslateService,
    private snackBarService: MatSnackBar,
    private dialogService: MatDialog,
    private store: Store<State>
  ) { }

  
  loadDrivers$ = createEffect(() => this.actions$.pipe(
    ofType(DriversActions.LOAD_DRIVERS),
    switchMap(() => this.driversService.getDrivers().pipe(
      map(drivers => new DriversActions.LoadDriversSuccess(drivers)),
      catchError(() => of(new DriversActions.LoadDriversFailure()))
    ))
  ));

  
  loadDriversFailure$ = createEffect(() => this.actions$.pipe(
    ofType(DriversActions.LOAD_DRIVERS_FAILURE),
    switchMap(() => this.translateService.get('drivers.loadDriversError').pipe(
      tap(errorMessage => this.snackBarService.open(errorMessage))
    ))
  ), { dispatch: false });

  
  editDriver$ = createEffect(() => this.actions$.pipe(
    ofType(DriversActions.EDIT_DRIVER),
    map(action => action.driverId),
    tap(driverId => this.dialogService.open(DriverDetailComponent, {
      width: '25rem',
      data: { driverId }
    }))
  ), { dispatch: false });

  
  loadDriver$ = createEffect(() => this.actions$.pipe(
    ofType(DriversActions.LOAD_DRIVER),
    map(action => action.driverId),
    switchMap(driverId => {
      const getDriver$ = of(driverId);

      const createDriver$ = getDriver$.pipe(
        filter(id => !id),
        switchMap(() => of({ } as Driver))
      );

      const loadDriver$ = getDriver$.pipe(
        filter(id => !!id),
        switchMap(id => this.driversService.getDriver(id))
      );

      return merge(createDriver$, loadDriver$).pipe(
        map(driver => new DriversActions.LoadDriverSuccess(driver)),
        catchError(() => of(new DriversActions.LoadDriverFailure()))
      );
    })
  ));

  
  loadDriverFailure$ = createEffect(() => this.actions$.pipe(
    ofType(DriversActions.LOAD_DRIVER_FAILURE),
    switchMap(() => this.translateService.get('drivers.loadDriverError').pipe(
      tap(errorMessage => this.snackBarService.open(errorMessage))
    ))
  ), { dispatch: false });

  
  persistDriver$ = createEffect(() => this.actions$.pipe(
    ofType(DriversActions.PERSIST_DRIVER),
    map(action => action.driver),
    switchMap(driver => {
      const persistDriver$ = of(driver['@id']);

      const createDriver$ = persistDriver$.pipe(
        filter(id => !id),
        switchMap(() => this.driversService.createDriver(driver))
      );

      const updateDriver$ = persistDriver$.pipe(
        filter(id => !!id),
        switchMap(() => this.driversService.updateDriver(driver))
      );

      return merge(createDriver$, updateDriver$).pipe(
        map(() => new DriversActions.PersistDriverSuccess()),
        catchError(() => of(new DriversActions.PersistDriverFailure()))
      );
    })
  ));

  
  persistDriverSuccess$ = createEffect(() => this.actions$.pipe(
    ofType(DriversActions.PERSIST_DRIVER_SUCCESS),
    tap(() => this.dialogService.closeAll()),
    map(() => new DriversActions.LoadDrivers())
  ));

  
  persistDriverFailure$ = createEffect(() => this.actions$.pipe(
    ofType(DriversActions.PERSIST_DRIVER_FAILURE),
    switchMap(() => this.translateService.get('drivers.persistDriverError').pipe(
      tap(errorMessage => this.snackBarService.open(errorMessage))
    ))
  ), { dispatch: false });

  
  deleteDriver$ = createEffect(() => this.actions$.pipe(
    ofType(DriversActions.DELETE_DRIVER),
    map(action => action.driverId),
    switchMap(id => this.driversService.deleteDriver(id).pipe(
      switchMap(() => [
        new DriversActions.DeleteDriverSuccess(),
        new DriversActions.LoadDrivers()
      ]),
      catchError(() => of(new DriversActions.DeleteDriverFailure()))
    ))
  ));

  
  deleteDriverFailure$ = createEffect(() => this.actions$.pipe(
    ofType(DriversActions.DELETE_DRIVER_FAILURE),
    switchMap(() => this.translateService.get('drivers.errors.delete').pipe(
      tap(errorMessage => this.snackBarService.open(errorMessage))
    ))
  ), { dispatch: false });

  
  deactivateDriver$ = createEffect(() => this.actions$.pipe(
    ofType(DriversActions.DEACTIVATE_DRIVER),
    map((action: DriversActions.DeactivateDriver) => action.driver),
    switchMap(driver => this.dialogService.open(DriverDeactivationConfirmationComponent, {
        data: {driver}
      }).afterClosed().pipe(
        map(confirmation => [confirmation, driver])
      )
    ),
    filter(confirmation => confirmation[0] === true),
    map(confirmation => confirmation[1] as Driver),
    switchMap(driver => this.driversService.deactivateDriver(driver)),
    tap(_ => this.store.dispatch(new DriversActions.LoadDrivers()))
  ), {dispatch: false});
}
